import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { WA_PHYSICS } from '../core/constants';

export interface RopeState {
  active: boolean;
  anchorX: number;
  anchorY: number;
  length: number;
  angle: number;
  angularVelocity: number;
  attached: boolean;
  dirX: number;
  dirY: number;
  speed: number;
  maxLength: number;
}

export const WeaponNinjaRope: WeaponDefinition = {
  id: 'rope',
  name: 'Ninja Rope',
  icon: 'rope.png',
  category: 'utility',
  maxAmmo: -1,
  maxUsesPerTurn: -1,
  powerCharging: false,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: false,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle } = ctx;

    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

    const ropeSpeed = 800;
    const maxRopeLength = 300;

    const startX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
    const startY = worm.position.y + WA_PHYSICS.WORM.HEIGHT / 2;

    const dirX = Math.cos(aimRad);
    const dirY = -Math.sin(aimRad);

    const ropeState: RopeState = {
      active: true,
      anchorX: startX,
      anchorY: startY,
      length: 0,
      angle: aimRad,
      angularVelocity: 0,
      attached: false,
      dirX,
      dirY,
      speed: ropeSpeed,
      maxLength: maxRopeLength,
    };

    (worm as any).ropeState = ropeState;

    return {
      success: true,
      entities: [],
      message: 'Rope fired!',
    };
  },
};

export function updateRopePhysics(worm: any, terrain: any, deltaTime: number): void {
  const ropeState = worm.ropeState as RopeState | undefined;
  if (!ropeState || !ropeState.active) return;

  if (!ropeState.attached) {
    // Defensive check: if direction/speed/maxLength are missing (e.g. from incomplete network sync),
    // deactivate the rope immediately to prevent NaN propagation and game freeze
    if (
      ropeState.dirX === undefined ||
      ropeState.dirY === undefined ||
      ropeState.speed === undefined ||
      ropeState.maxLength === undefined
    ) {
      ropeState.active = false;
      worm.ropeState = undefined;
      return;
    }

    ropeState.length += ropeState.speed * deltaTime;

    if (ropeState.length >= ropeState.maxLength) {
      ropeState.active = false;
      return;
    }

    const tipX = ropeState.anchorX + ropeState.dirX * ropeState.length;
    const tipY = ropeState.anchorY + ropeState.dirY * ropeState.length;

    if (terrain.isSolid(tipX, tipY)) {
      ropeState.attached = true;
      ropeState.anchorX = tipX;
      ropeState.anchorY = tipY;

      const dx = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2 - tipX;
      const dy = worm.position.y + WA_PHYSICS.WORM.HEIGHT / 2 - tipY;
      ropeState.length = Math.sqrt(dx * dx + dy * dy);
      ropeState.angle = Math.atan2(dy, dx);

      const tangentVelX = -Math.sin(ropeState.angle) * worm.velocity.x;
      const tangentVelY = Math.cos(ropeState.angle) * worm.velocity.y;
      ropeState.angularVelocity = (tangentVelX + tangentVelY) / ropeState.length;
    }
  } else {
    const gravity = WA_PHYSICS.GRAVITY;
    const gravityTorque = (gravity * Math.cos(ropeState.angle)) / ropeState.length;
    ropeState.angularVelocity += gravityTorque * deltaTime;

    const damping = 0.995;
    ropeState.angularVelocity *= damping;

    ropeState.angle += ropeState.angularVelocity * deltaTime;

    const newX = ropeState.anchorX + Math.cos(ropeState.angle) * ropeState.length;
    const newY = ropeState.anchorY + Math.sin(ropeState.angle) * ropeState.length;

    worm.position.x = newX - WA_PHYSICS.WORM.WIDTH / 2;
    worm.position.y = newY - WA_PHYSICS.WORM.HEIGHT / 2;

    worm.velocity.x = -Math.sin(ropeState.angle) * ropeState.angularVelocity * ropeState.length;
    worm.velocity.y = Math.cos(ropeState.angle) * ropeState.angularVelocity * ropeState.length;

    worm.isGrounded = false;
  }
}

export function releaseRope(worm: any): void {
  const ropeState = worm.ropeState as RopeState | undefined;
  if (!ropeState) return;

  if (ropeState.attached) {
    const launchBoost = 1.2;
    worm.velocity.x *= launchBoost;
    worm.velocity.y *= launchBoost;
  }

  ropeState.active = false;
  worm.ropeState = undefined;
}

export function extendRope(worm: any, amount: number): void {
  const ropeState = worm.ropeState as RopeState | undefined;
  if (!ropeState || !ropeState.attached) return;

  const newLength = ropeState.length + amount;
  if (newLength > 20 && newLength < 400) {
    ropeState.length = newLength;
  }
}

export function applyRopeInput(
  worm: any,
  left: boolean,
  right: boolean,
  up: boolean,
  down: boolean,
): void {
  const ropeState = worm.ropeState as RopeState | undefined;
  if (!ropeState || !ropeState.attached) return;

  const swingForce = 5;
  if (left) {
    ropeState.angularVelocity += swingForce / ropeState.length;
  }
  if (right) {
    ropeState.angularVelocity -= swingForce / ropeState.length;
  }

  if (up) {
    extendRope(worm, -3);
  }
  if (down) {
    extendRope(worm, 3);
  }
}

export function isRopeActive(worm: any): boolean {
  const ropeState = worm.ropeState as RopeState | undefined;
  return ropeState?.active ?? false;
}

export function getRopeRenderData(
  worm: any,
): { startX: number; startY: number; endX: number; endY: number } | null {
  const ropeState = worm.ropeState as RopeState | undefined;
  if (!ropeState || !ropeState.active) return null;

  const wormX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
  const wormY = worm.position.y + WA_PHYSICS.WORM.HEIGHT / 2;

  if (ropeState.attached) {
    return {
      startX: ropeState.anchorX,
      startY: ropeState.anchorY,
      endX: wormX,
      endY: wormY,
    };
  } else {
    return {
      startX: wormX,
      startY: wormY,
      endX: ropeState.anchorX + ropeState.dirX * ropeState.length,
      endY: ropeState.anchorY + ropeState.dirY * ropeState.length,
    };
  }
}
